import * as R from 'ramda';
import { useDispatch } from 'react-redux';
import React, { useCallback } from 'react';
// common
import { openModal } from '../../../../components/modal/actions';
// features
import RouteMap from '../../../geo-fencing-location/components/route-map';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
import { ENUMS } from '../../../../constants/enums';
// icons
import * as I from '../../../../svgs';
// ui
import { IconWrapper } from '../../../../ui';
//////////////////////////////////////////////////

const iconColor = G.getTheme('colors.white');

const settings = {
  [GC.FIELD_TEL_PRIMARY_REFERENCE_VALUE]: {
    width: 150,
    filterType: 'string',
    name: 'titles:tel-number',
    useExpandedContainer: true,
    guidPropName: GC.FIELD_TEL_GUID,
    type: GC.PAGE_DISPATCH_DETAILS_NEW_LOAD,
  },
  [GC.FIELD_LOG_DATE]: {
    type: 'date',
    name: 'titles:date',
  },
  [`${GC.SYSTEM_OBJECT_GEOFENCING_LOCATION}.${GC.FIELD_NAME}`]: {
    width: 150,
    name: 'titles:location-name',
  },
  [GC.FIELD_TYPE]: {
    type: 'enum',
    name: 'titles:type',
    filterType: 'string',
    options: [
      {
        value: ENUMS.ENUM_ARRIVED,
        label: G.getEnumLocale(ENUMS.ENUM_ARRIVED),
      },
      {
        value: ENUMS.ENUM_DEPARTED,
        label: G.getEnumLocale(ENUMS.ENUM_DEPARTED),
      },
    ],
  },
  [GC.FIELD_EVENT_TYPE]: {
    type: 'enum',
    filterType: 'string',
    name: 'titles:event-type',
    options: [
      {
        value: GC.EVENT_TYPE_PICKUP,
        label: G.getEnumLocale(GC.EVENT_TYPE_PICKUP),
      },
      {
        value: GC.EVENT_TYPE_DROP,
        label: G.getEnumLocale(GC.EVENT_TYPE_DROP),
      },
    ],
  },
  [GC.FIELD_SOURCE]: {
    name: 'titles:source',
  },
  [GC.FIELD_CITY]: {
    name: 'titles:city',
  },
  [GC.FIELD_STATE]: {
    width: 120,
    name: 'titles:state',
  },
  [GC.FIELD_ZIP]: {
    width: 120,
    name: 'titles:zip',
  },
  [GC.FIELD_COUNTRY]: {
    width: 150,
    name: 'titles:country',
  },
  [GC.FIELD_LATITUDE]: {
    width: 150,
    filterType: 'number',
    name: 'titles:latitude',
  },
  [GC.FIELD_LONGITUDE]: {
    width: 150,
    filterType: 'number',
    name: 'titles:longitude',
  },
  [GC.FIELD_CREATED_DATE]: {
    type: 'date',
    name: 'titles:created-date',
  },
  [GC.FIELD_LAST_MODIFIED_DATE]: {
    type: 'date',
    name: 'titles:updated-date',
  },
  [GC.FIELD_BATTERY_STATUS]: {
    width: 150,
    type: 'enum',
    filterType: 'string',
    name: 'titles:battery-status',
    options: [
      {
        value: ENUMS.ENUM_LOW,
        label: G.getEnumLocale(ENUMS.ENUM_LOW),
      },
      {
        value: ENUMS.ENUM_GOOD,
        label: G.getEnumLocale(ENUMS.ENUM_GOOD),
      },
      {
        value: ENUMS.ENUM_NO_CHARGE,
        label: G.getEnumLocale(ENUMS.ENUM_NO_CHARGE),
      },
    ],
  },
};

const columnSettings = R.map(
  R.pick([
    'type',
    'name',
    'width',
    'sortable',
    'searchable',
    'guidPropName',
    'customComponent',
    'useExpandedContainer',
  ]),
  settings,
);

const placeholder = { text: 'Choose', key: 'titles:choose' };

const filterProps = R.mapObjIndexed((
  { name, options, collection, filterType, disableFilter, type = 'string' },
  value,
) => ({
  value,
  options,
  collection,
  placeholder,
  disableFilter,
  type: R.or(filterType, type),
  [GC.FIELD_NAME]: G.getWindowLocale(name),
}), settings);

export const reportFields = G.mapIndexed((name, sequence) => ({ name, sequence }), R.keys(settings));

const makeRequestPayload = (props) => {
  const { primaryObjectGuid, primaryObjectBranchGuid } = props;

  return {
    [GC.CURRENT_BRANCH]: primaryObjectBranchGuid,
    params: { [GC.FIELD_TRAILER_GUID]: primaryObjectGuid },
    fields: G.addRequiredFields(reportFields, [GC.FIELD_TEL_GUID]),
    searchCriteria: [
      {
        operation: 'equal',
        dataType: 'string',
        stringValue: primaryObjectGuid,
        propertyName: GC.FIELD_TRAILER_GUID,
      },
    ],
  };
};

const makeExportRequestPayload = ({ primaryObjectGuid }) => ({
  queryParams: { [GC.FIELD_TRAILER_GUID]: primaryObjectGuid },
});

const AdditionalFormGroupTitleComponent = ({
  itemList,
}) => {
  const dispatch = useDispatch();

  const handleShowMap = useCallback(() => {
    const component = (
      <RouteMap routeHistoryList={itemList} />
    );

    const modal = {
      p: '0px',
      component,
      options: {
        movable: false,
        height: 'auto',
        width: '660px',
        withCloseIcon: true,
        title: G.getWindowLocale('titles:trailer-tracking', 'Trailer Tracking'),
      },
    };

    dispatch(openModal(modal));
  }, [itemList]);

  if (G.isNilOrEmpty(itemList)) return null;

  return (
    <IconWrapper
      ml={10}
      cursor='pointer'
      onClick={handleShowMap}
      title={G.getWindowLocale('actions:show-on-a-map', 'Show Map')}
    >
      {I.onMap(iconColor, 18, 18)}
    </IconWrapper>
  );
};

export const FILTER_PARAMS = R.values(filterProps);

export const trailerTrackingSettings = {
  filterProps,
  columnSettings,
  isReport: true,
  actionsPicker: [],
  noExportable: false,
  useSearchableColumns: true,
  withResizableColumns: true,
  useNewTitleFilterInputs: true,
  groupName: 'trailerTrackingList',
  AdditionalFormGroupTitleComponent,
  reportType: GC.TRAILER_MILESTONE_LOG_REPORT,
  additionalTableSettings: { searchableTitles: true },
  formGroupTitleArr: ['titles:trailer-tracking', 'Trailer Tracking'],
  endpoints: {
    list: 'trailerMilestoneLogList',
    export: 'trailerMilestoneLogListExport',
  },
  // helpers
  makeRequestPayload,
  makeExportRequestPayload,
};
